import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Employee, InvitationResult, RoleSummary, UserList } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';

/**
 * Benutzerzugang für einen Mitarbeiter einladen (KON-086). Name und E-Mail kommen aus der Karte und lassen sich ändern. Der Mitarbeiter bekommt
 * einen Link, über den er sein Passwort selbst festlegt, und ist danach mit dem Zugang verknüpft. Gezeigt wird der Bereich nur, wenn der
 * Benutzer Mitarbeiter bearbeiten und Benutzer verwalten darf. Was erlaubt ist, entscheidet der Server.
 */
export function EmployeeInvite({ employee, onInvited }: { employee: Employee; onInvited: (result: InvitationResult, mailRequested: boolean) => void }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(`${employee.firstName} ${employee.lastName}`);
  const [email, setEmail] = useState(employee.email ?? '');
  const [roleKey, setRoleKey] = useState('');
  const [sendMail, setSendMail] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const roles = useQuery({ queryKey: ['roles'], queryFn: async () => (await api<{ data: RoleSummary[] }>('GET', '/users/roles')).data, enabled: open });
  // Ob Wirkia E-Mails verschicken kann, steht in der Benutzerliste
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserList>('GET', '/users'), enabled: open });
  const mailEnabled = users.data?.meta.mailEnabled ?? false;

  const invite = useMutation({
    mutationFn: () => api<{ data: InvitationResult }>('POST', `/employees/${employee.id}/invite-user`, { name: name.trim(), email: email.trim(), roleKey, sendMail: sendMail && mailEnabled }),
    onSuccess: (res) => {
      setOpen(false);
      client.invalidateQueries({ queryKey: ['users'] });
      client.invalidateQueries({ queryKey: ['employee-user-options'] });
      onInvited(res.data, sendMail && mailEnabled);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFormError('Bitte prüfen Sie Ihre Eingaben.');
      } else {
        setFormError(e instanceof ApiError ? e.message : 'Die Einladung konnte nicht angelegt werden.');
      }
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    invite.mutate();
  }

  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <CollapsibleCard title="Zugang zu Wirkia" ariaLabel="Benutzerzugang" className="invite-box" open={open} onOpenChange={setOpen}>
      <p className="muted small">
        {employee.firstName} hat keinen Zugang zu Wirkia. Über die Einladung legt {employee.firstName} das Passwort selbst fest. Name und E-Mail übernimmt Wirkia aus dieser Karte.
      </p>
      {open && (
        <form onSubmit={submit} noValidate>
          {formError && <div className="alert alert--error" role="alert">{formError}</div>}
          {!employee.email && <div className="alert alert--ok">Auf der Karte ist keine E-Mail-Adresse hinterlegt. Tragen Sie sie hier ein, sie wird nicht auf der Karte gespeichert.</div>}
          <label className="field">
            <span>Name <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" aria-invalid={!!errors.name} />
            {error('name')}
          </label>
          <label className="field">
            <span>E-Mail <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" aria-invalid={!!errors.email} />
            {error('email')}
          </label>
          <label className="field">
            <span>Rolle <abbr title="Pflichtfeld">*</abbr></span>
            <select className="input" value={roleKey} onChange={(e) => setRoleKey(e.target.value)} aria-invalid={!!errors.roleKey}>
              <option value="">Bitte wählen</option>
              {(roles.data ?? []).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
            {error('roleKey')}
            {roles.isError && <small className="field__error" role="alert">Die Rollen konnten nicht geladen werden.</small>}
          </label>
          {mailEnabled && (
            <label className="check">
              <input type="checkbox" checked={sendMail} onChange={(e) => setSendMail(e.target.checked)} />
              <span>Einladung zusätzlich per E-Mail schicken</span>
            </label>
          )}
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={invite.isPending || !roleKey || name.trim().length < 2 || !email.trim()}>
              {invite.isPending ? 'Wird angelegt …' : 'Einladung anlegen'}
            </button>
            <button className="btn btn--secondary" type="button" onClick={() => setOpen(false)}>Abbrechen</button>
          </div>
        </form>
      )}
    </CollapsibleCard>
  );
}
